import { z } from 'zod';
import { t } from '@lingui/core/macro';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import type { GuardName } from '@duke-hq/svc-api/lib/automationRules/registry';

export type AutomationRuleListItem = RouterOutputs['automationRules']['list'][number];

export type RuleTaskKind = AutomationRuleListItem['taskKind'];

type RuleGuard = NonNullable<AutomationRuleListItem['guards']>[number];

type AnchorOffsetsTrigger = Extract<
  NonNullable<AutomationRuleListItem['trigger']>,
  { form: 'anchorOffsets' }
>;

export type RuleAnchor = AnchorOffsetsTrigger['anchor'];

// Indexed by the stored day-of-week number (0 = Sunday, Sydney time).
function sydneyWeekday(dayOfWeek: number): string | undefined {
  return [t`Sunday`, t`Monday`, t`Tuesday`, t`Wednesday`, t`Thursday`, t`Friday`, t`Saturday`][
    dayOfWeek
  ];
}

export function describeTrigger(rule: AutomationRuleListItem): string {
  if (rule.trigger == null) {
    return t`Invalid rule`;
  }
  switch (rule.trigger.form) {
    case 'daily':
      return t`Every day`;
    case 'event': {
      const eventType = rule.trigger.eventType;
      return eventType == null ? t`On an event` : t`On ${eventType}`;
    }
    case 'weekly': {
      const dayOfWeek = rule.trigger.dayOfWeekSydney;
      const day = sydneyWeekday(dayOfWeek);
      return day == null ? t`Weekly on day ${dayOfWeek}` : t`Weekly on ${day}`;
    }
    case 'anchorOffsets': {
      const days = rule.trigger.offsetDays.join(', ');
      return rule.trigger.offsetDays.length === 1 ? t`Day ${days}` : t`Days ${days}`;
    }
  }
}

// A rule may legally repeat a guard with different params (two inactivity
// windows, say), so a render key needs the params too, plus a counter for the
// degenerate case of an exactly repeated guard.
export function withRenderKeys(
  guards: NonNullable<AutomationRuleListItem['guards']>,
): Array<{ guard: RuleGuard; key: string }> {
  const seen = new Map<string, number>();
  return guards.map((guard) => {
    const base = `${guard.negate ? 'not-' : ''}${guard.guard}-${JSON.stringify(guard.params)}`;
    const repeat = seen.get(base) ?? 0;
    seen.set(base, repeat + 1);
    return { guard, key: repeat === 0 ? base : `${base}#${repeat}` };
  });
}

// -- Guards ------------------------------------------------------------------

const guardParamsSchema = z.object({
  days: z.number().optional(),
  atLeast: z.number().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
});

// Params reach the client as untyped json. A rule whose params do not match
// its guard is already flagged Invalid, so a missing number renders as "?"
// rather than hiding the condition.
function guardParams(params: unknown) {
  const parsed = guardParamsSchema.safeParse(params);
  return parsed.success ? parsed.data : {};
}

// Support staff read this page, so every condition is spelled out as a
// sentence rather than shown as the registry identifier. Both polarities are
// written out: a generic "not" in front of most of these reads badly.
const guardDescribers: Record<GuardName, (guard: RuleGuard) => string> = {
  courseAccredited: ({ negate }) =>
    negate ? t`Course is not accredited` : t`Course is accredited`,
  courseVocational: ({ negate }) =>
    negate ? t`Course is not vocational` : t`Course is vocational`,
  providerBillable: ({ negate }) =>
    negate ? t`Provider is not billable` : t`Provider is billable`,
  llnCompleted: ({ negate }) =>
    negate
      ? t`Student has not finished their LLN assessment`
      : t`Student has finished their LLN assessment`,
  usiVerified: ({ negate }) =>
    negate ? t`Student's USI is not verified` : t`Student's USI is verified`,
  studentPhoneOnFile: ({ negate }) =>
    negate ? t`Student has no phone number on file` : t`Student has a phone number on file`,
  activeWorkPlacement: ({ negate }) =>
    negate ? t`Student is not on a work placement` : t`Student is on a work placement`,
  awaitingSemesterTwoRelease: ({ negate }) =>
    negate
      ? t`Student is not waiting for semester 2 to be released`
      : t`Student has finished semester 1 and is waiting for semester 2`,
  unitsInRevision: ({ negate, params }) => {
    const atLeast = guardParams(params).atLeast ?? '?';
    return negate
      ? t`Fewer than ${atLeast} units in revision, worked on in the last 30 days`
      : t`At least ${atLeast} units in revision, worked on in the last 30 days`;
  },
  revisionReminderSentWithinDays: ({ negate, params }) => {
    const days = guardParams(params).days ?? '?';
    return negate
      ? t`No revision reminder sent in the last ${days} days`
      : t`A revision reminder was sent in the last ${days} days`;
  },
  workPlacementChecklistAccepted: ({ negate }) =>
    negate
      ? t`Pre-placement checklist has not been accepted`
      : t`Pre-placement checklist has been accepted`,
  incompleteWorkPlacementExists: ({ negate }) =>
    negate
      ? t`No placement is still collecting pre-placement documents`
      : t`A placement is still collecting pre-placement documents`,
  workPlacementChecklistDueWithinDays: ({ negate, params }) => {
    const days = guardParams(params).days ?? '?';
    // The guard is false whenever the deadline is unknown, so the negated
    // wording cannot claim semester 2 is far off.
    return negate
      ? t`Semester 2 is more than ${days} days away, or there is no checklist deadline`
      : t`Semester 2 starts within ${days} days (or has started), the checklist deadline`;
  },
  inactiveBetweenDays: ({ negate, params }) => {
    const { min = '?', max = '?' } = guardParams(params);
    return negate
      ? t`Inactive for less than ${min} or more than ${max} days`
      : t`Inactive for ${min} to ${max} days`;
  },
  lastActiveExactlyDaysAgo: ({ negate, params }) => {
    const days = guardParams(params).days ?? '?';
    return negate
      ? t`Has been active, but not exactly ${days} days ago`
      : t`Last active exactly ${days} days ago, or never active`;
  },
};

// Widened view for lookups by the untyped guard name stored on the rule row;
// entries stay fully typed above.
const describers: Record<string, ((guard: RuleGuard) => string) | undefined> = guardDescribers;

export function describeGuard(guard: RuleGuard): string {
  const describe = describers[guard.guard];
  if (describe != null) {
    return describe(guard);
  }
  // A guard added to the registry without a sentence here still reads, using
  // the registry's own wording.
  const fallback = guard.description ?? guard.guard;
  return guard.negate ? t`Not: ${fallback}` : fallback;
}

// -- Anchors -----------------------------------------------------------------

export function describeAnchor(anchor: RuleAnchor): string {
  switch (anchor) {
    case 'enrolmentCreated':
      return t`Day 1 is the day the student enrolled`;
    case 'enrolmentEffectiveStart':
      return t`Day 1 is the course start date, or the enrolment date if that is later`;
    case 'effectiveLastActivity':
      return t`Day 1 is the student's last activity — reset by a restoration, the end of a work placement, or a semester 2 release`;
    case 'unitReleased':
      return t`Day 1 is the day the unit is released`;
  }
}

// -- Scheduling --------------------------------------------------------------

// The stored hour is a bare 0-23 Sydney hour, so "4:00" alone reads as
// ambiguous 24-hour time. Spell out am/pm.
export function describeProcessHour(hour: number): string {
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return hour < 12 ? t`${hour12}:00am` : t`${hour12}:00pm`;
}

export function describeScope(rule: AutomationRuleListItem): string {
  if (rule.scope == null) {
    return '—';
  }
  return rule.scope === 'unit' ? t`Unit` : t`Enrolment`;
}

export function describeDedupe(rule: AutomationRuleListItem): string {
  switch (rule.dedupe) {
    case 'ONCE_PER_DAY':
      return t`Once per day`;
    case 'ONCE_PER_OBJECT':
      return t`Once ever`;
    case 'WHILE_OPEN':
      return t`Not while one is open`;
    case 'ONCE_PER_WINDOW': {
      const days = rule.dedupeWindowDays;
      return days == null ? t`Once per window` : t`Once every ${days} days`;
    }
  }
}

export const RULE_STATUS_KEYS = ['enabled', 'disabled', 'invalid'] as const;

export type RuleStatusKey = (typeof RULE_STATUS_KEYS)[number];

export function ruleStatus(rule: AutomationRuleListItem): {
  key: RuleStatusKey;
  label: string;
  colorScheme: 'green' | 'gray' | 'red';
} {
  // Invalid takes precedence over enabled/disabled: a rule the parser rejects
  // never fires, so that is the state worth showing first.
  if (rule.parseError != null) {
    return { key: 'invalid', label: t`Invalid`, colorScheme: 'red' };
  }
  return rule.enabled
    ? { key: 'enabled', label: t`Enabled`, colorScheme: 'green' }
    : { key: 'disabled', label: t`Disabled`, colorScheme: 'gray' };
}
